import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  //LinearProgress
} from '@mui/material';
import { 
    PieChart, Pie, Cell, ResponsiveContainer, 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend 
  } from 'recharts';
import { dashboardService } from '../../services/api';

const ManagerDashboard = ({ data }) => {
  const [dateRange, setDateRange] = useState('current');
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    teamSize: data.dashboardData?.teamSize || 0,
    enrollmentStats: data.dashboardData?.enrollmentStats || {
      enrolledLearners: 0,
      notEnrolledLearners: 0,
      totalEnrollments: 0
    },
    inProgressStats: data.dashboardData?.inProgressStats || {
      activeLearners: 0,
      notActiveLearners: 0,
      totalViews: 0
    },
    completionStats: data.dashboardData?.completionStats || {
      completedLearners: 0,
      notCompletedLearners: 0,
      totalCompletions: 0
    }
  });
  const [teamSummary, setTeamSummary] = useState({
    overview: {
      totalTeamMembers: 0,
      enrolledLearners: 0,
      totalEnrollments: 0,
      viewsLearners: 0,
      totalViews: 0,
      completionsLearners: 0,
      totalCompletions: 0
    },
    members: []
  });
  const [courseData, setCourseData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data.dashboardData) {
      setStats({
        teamSize: data.dashboardData.teamSize || 0,
        enrollmentStats: data.dashboardData.enrollmentStats || {
          enrolledLearners: 0,
          notEnrolledLearners: 0,
          totalEnrollments: 0
        },
        inProgressStats: data.dashboardData.inProgressStats || {
          activeLearners: 0,
          notActiveLearners: 0,
          totalViews: 0
        },
        completionStats: data.dashboardData.completionStats || {
          completedLearners: 0,
          notCompletedLearners: 0,
          totalCompletions: 0
        }
      });
    }
    
    // Fetch initial team summary and course data
    fetchTeamSummary(getDateRange('current'));
    fetchCourseData(getDateRange('current'), 'enrollments');
  }, [data.dashboardData]);

  const getDateRange = (selectedRange) => {
    const today = new Date();
    let startDate, endDate;
    
    switch(selectedRange) {
      case 'current':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'last3months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'lastyear':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
    }
    
    return { 
      startDate: formatDateForDB(startDate), 
      endDate: formatDateForDB(endDate) 
    };
  };

  const formatDateForDB = (date) => date.toISOString().split('T')[0];

  const fetchTeamSummary = async (dateRangeObj) => {
    try {
      setLoading(true);
      const response = await dashboardService.getTeamSummary(
        dateRangeObj.startDate, 
        dateRangeObj.endDate
      );
      
      if (response.data) {
        setTeamSummary(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseData = async (dateRangeObj, dataType) => {
    try {
      setLoading(true);
      const response = await dashboardService.getCourseData(
        dateRangeObj.startDate, 
        dateRangeObj.endDate,
        dataType
      );
      
      if (response.data) {
        setCourseData(response.data);
      }
    } catch (error) {
      console.error(`Failed to fetch ${dataType} data:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = async (event) => {
    const newRange = event.target.value;
    setDateRange(newRange);
    
    try {
      setLoading(true);
      const dateRangeObj = getDateRange(newRange);
      
      // Fetch team stats
      const statsResponse = await dashboardService.getTeamStats(
        dateRangeObj.startDate, 
        dateRangeObj.endDate
      );
      
      if (statsResponse.data) {
        setStats({
          ...stats,
          enrollmentStats: statsResponse.data.enrollmentStats || stats.enrollmentStats,
          inProgressStats: statsResponse.data.inProgressStats || stats.inProgressStats,
          completionStats: statsResponse.data.completionStats || stats.completionStats,
          teamSize: statsResponse.data.totalLearners || stats.teamSize
        });
      }
      
      // Fetch team summary
      fetchTeamSummary(dateRangeObj);
      
      // Fetch course data based on active tab
      let dataType = 'enrollments';
      if (activeTab === 'views') dataType = 'views';
      if (activeTab === 'completions') dataType = 'completions';
      
      fetchCourseData(dateRangeObj, dataType);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    if (newValue !== 'overview') {
      const dateRangeObj = getDateRange(dateRange);
      fetchCourseData(dateRangeObj, newValue);
    }
  };

  // Prepare data for MUI X Charts
  const enrollmentPieData = [
    { id: 0, value: stats.enrollmentStats.enrolledLearners, label: 'Enrolled' },
    { id: 1, value: stats.enrollmentStats.notEnrolledLearners, label: 'Not Enrolled' }
  ];

  const inProgressPieData = [
    { id: 0, value: stats.inProgressStats.activeLearners, label: 'Active' },
    { id: 1, value: stats.inProgressStats.notActiveLearners, label: 'Inactive' }
  ];

  const completionsPieData = [
    { id: 0, value: stats.completionStats.completedLearners, label: 'Completed' },
    { id: 1, value: stats.completionStats.notCompletedLearners, label: 'Not Completed' }
  ];

  // Prepare bar chart data
  const barChartData = courseData.slice(0, 5).map(item => {
    if (activeTab === 'enrollments') return item.enrollments;
    if (activeTab === 'views') return item.views;
    if (activeTab === 'completions') return item.completions;
    return 0;
  });

  const barChartLabels = courseData.slice(0, 6).map(item => item.name);
  const COLORS = {
    enrollments: ['#0088FE', '#E0E0E0'],
    views: ['#FF8042', '#E0E0E0'],
    completions: ['#00C49F', '#E0E0E0']
  };
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Welcome, {data.user.name}.
        </Typography>

        {/* Learning Summary Section */}
        <Card sx={{ mt: 3, mb: 4, borderRadius: '8px' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Learning Summary</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="body1">
                <strong>Team Size:</strong> {stats.teamSize}
              </Typography>
              
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  size="small"
                >
                  <MenuItem value="current">Current Month to Today</MenuItem>
                  <MenuItem value="last3months">Last 3 Months</MenuItem>
                  <MenuItem value="lastyear">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* {loading && <LinearProgress sx={{ mb: 2 }} />} */}

            <Grid container spacing={3}>
              {/* Enrollments Chart */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', p: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <Box sx={{ position: 'relative', height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={enrollmentPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {enrollmentPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS.enrollments[index % COLORS.enrollments.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}>
                      <Typography sx={{ color: '#0088FE', fontWeight: 'bold' }}>
                        {stats.enrollmentStats.totalEnrollments}
                      </Typography>
                      <Typography variant="body2">Enrollments</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        By {stats.enrollmentStats.enrolledLearners} Learners
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* In Progress Chart */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', p: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <Box sx={{ position: 'relative', height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={inProgressPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {inProgressPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS.views[index % COLORS.views.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}>
                      <Typography sx={{ color: '#FF8042', fontWeight: 'bold' }}>
                        {stats.inProgressStats.totalViews}
                      </Typography>
                      <Typography variant="body2">In Progress</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        By {stats.inProgressStats.activeLearners} Learners
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* Completions Chart */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', p: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <Box sx={{ position: 'relative', height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={completionsPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {completionsPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS.completions[index % COLORS.completions.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}>
                      <Typography sx={{ color: '#00C49F', fontWeight: 'bold' }}>
                        {stats.completionStats.totalCompletions}
                      </Typography>
                      <Typography variant="body2">Completions</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        By {stats.completionStats.completedLearners} Learners
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Team Performance Section */}
        <Card sx={{ mb: 4, borderRadius: '8px' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Team Performance</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="team performance tabs">
                <Tab label="Overview" value="overview" />
                <Tab label="Enrollments" value="enrollments" />
                <Tab label="In Progress" value="views" />
                <Tab label="Completions" value="completions" />
              </Tabs>
            </Box>

            {/* {loading && <LinearProgress sx={{ mb: 2 }} />} */}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team Member</TableCell>
                      <TableCell align="center">Enrollments</TableCell>
                      <TableCell align="center">In Progress</TableCell>
                      <TableCell align="center">Completions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamSummary.members.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell component="th" scope="row">{member.name}</TableCell>
                        <TableCell align="center">{member.enrollments}</TableCell>
                        <TableCell align="center">{member.views}</TableCell>
                        <TableCell align="center">{member.completions}</TableCell>
                      </TableRow>
                    ))}
                    {teamSummary.members.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No team members data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Course Data Tabs */}
            {activeTab !== 'overview' && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ height: 300, width: '100%' }}>
                  {barChartLabels.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={courseData.slice(0, 5).map((item, index) => ({
                            name: item.name,
                            value: activeTab === 'enrollments' ? item.enrollments : 
                                  activeTab === 'views' ? item.views : item.completions
                          }))}
                          margin={{ top: 10, right: 10, bottom: 70, left: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            angle={45} 
                            textAnchor="start"
                            height={70}
                            interval={0}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar 
                            dataKey="value" 
                            name={activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} 
                            fill={
                              activeTab === 'enrollments' ? '#4694c0' : 
                              activeTab === 'views' ? '#FF9800' : '#4CAF50'
                            } 
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px'
                      }}>
                        <Typography>No data available</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', maxHeight: 300, overflow: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Course/Learning Path</TableCell>
                          <TableCell align="center">Type</TableCell>
                          <TableCell align="center">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {courseData.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row">{item.name}</TableCell>
                            <TableCell align="center">{item.type || 'course'}</TableCell>
                            <TableCell align="center">
                              {activeTab === 'enrollments' ? item.enrollments : 
                               activeTab === 'views' ? item.views : 
                               item.completions}
                            </TableCell>
                          </TableRow>
                        ))}
                        {courseData.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No data available</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ManagerDashboard;
